import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Tag } from "lucide-react";
import { api } from "../lib/api";
import { useShop, money, qtyLabel } from "../lib/shop";
import { FulfilmentPicker, FULFILMENT_LABELS } from "../components/FulfilmentPicker";
import { toast } from "sonner";

export default function Basket() {
    const { basket, updateQty, removeFromBasket, settings } = useShop();
    const [code, setCode] = useState("");
    const [appliedCode, setAppliedCode] = useState("");
    const [fulfilment, setFulfilment] = useState("collection");
    const [quote, setQuote] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!basket.length) {
            setQuote(null);
            return;
        }
        api.post("/shop/quote", {
            items: basket.map((i) => ({ product_id: i.product_id, variation: i.variation, qty: i.qty })),
            discount_code: appliedCode || undefined,
            fulfilment,
        })
            .then((r) => setQuote(r.data))
            .catch((err) => {
                setQuote(null);
                toast.error(err.response?.data?.detail || "Could not price your basket");
            });
    }, [basket, appliedCode, fulfilment]);

    const applyCode = async () => {
        if (!code.trim()) return;
        try {
            await api.post("/shop/quote", {
                items: basket.map((i) => ({ product_id: i.product_id, variation: i.variation, qty: i.qty })),
                discount_code: code.trim(),
                fulfilment,
            });
            setAppliedCode(code.trim().toUpperCase());
            toast.success("Discount applied");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invalid discount code");
        }
    };

    if (!basket.length)
        return (
            <main className="min-h-[50vh] bg-slate-50 flex items-center justify-center" data-testid="basket-empty">
                <div className="text-center px-4">
                    <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-3">Your basket is empty</h1>
                    <p className="text-slate-500 mb-6">Browse our products and add something tasty.</p>
                    <Link to="/products" data-testid="basket-shop-link"
                        className="inline-flex items-center rounded-full bg-[#0033A0] px-8 py-3 font-heading font-bold text-white hover:bg-[#002277] transition-colors">
                        Explore Our Products
                    </Link>
                </div>
            </main>
        );

    return (
        <main data-testid="basket-page" className="bg-slate-50 min-h-screen py-10 md:py-16">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mb-8" data-testid="basket-heading">
                    Your Basket
                </h1>
                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-4" data-testid="basket-items">
                        {basket.map((item) => (
                            <div key={`${item.product_id}|${item.variation || ""}`}
                                data-testid={`basket-item-${item.product_id}`}
                                className="bg-white rounded-2xl border border-slate-200 p-4 flex gap-4 items-center">
                                <img src={item.image} alt="" className="h-20 w-20 rounded-xl object-cover bg-slate-100 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-heading font-bold text-slate-900 truncate">
                                        {item.name}
                                        {item.variation && <span className="text-slate-500 font-normal"> · {item.variation}</span>}
                                    </p>
                                    <p className="text-sm text-slate-500">{money(item.price)} {item.unit !== "each" ? item.unit : ""}</p>
                                    <div className="mt-2 flex items-center gap-3">
                                        <div className="flex items-center rounded-full border border-slate-200">
                                            <button type="button" aria-label="Decrease" data-testid={`qty-minus-${item.product_id}`}
                                                onClick={() => updateQty(item.product_id, item.variation, item.qty - (item.sold_by_weight ? 0.25 : 1))}
                                                className="p-2 text-[#0033A0]"><Minus className="h-4 w-4" /></button>
                                            <span className={`${item.sold_by_weight ? "w-14" : "w-8"} text-center text-sm font-bold`} data-testid={`qty-${item.product_id}`}>{qtyLabel(item)}</span>
                                            <button type="button" aria-label="Increase" data-testid={`qty-plus-${item.product_id}`}
                                                onClick={() => updateQty(item.product_id, item.variation, item.qty + (item.sold_by_weight ? 0.25 : 1))}
                                                className="p-2 text-[#0033A0]"><Plus className="h-4 w-4" /></button>
                                        </div>
                                        <button type="button" aria-label={`Remove ${item.name}`} data-testid={`remove-${item.product_id}`}
                                            onClick={() => removeFromBasket(item.product_id, item.variation)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-full">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                <p className="font-heading font-extrabold text-[#0033A0] whitespace-nowrap">{money(item.price * item.qty)}</p>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 p-6 h-fit" data-testid="basket-summary">
                        <h2 className="font-heading font-extrabold text-lg text-slate-900 mb-4">Summary</h2>

                        <div className="mb-4">
                            <p className="text-sm font-bold text-slate-900 mb-2">How would you like your order?</p>
                            <FulfilmentPicker value={fulfilment} onChange={setFulfilment} testIdPrefix="basket-fulfilment" />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="discount-code" className="text-sm font-bold text-slate-900 flex items-center gap-1 mb-2">
                                <Tag className="h-4 w-4 text-[#0033A0]" /> Discount code
                            </label>
                            <div className="flex gap-2">
                                <input id="discount-code" value={code} onChange={(e) => setCode(e.target.value)}
                                    placeholder="Enter code" data-testid="discount-input"
                                    className="flex-1 min-w-0 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033A0]" />
                                <button type="button" onClick={applyCode} data-testid="discount-apply"
                                    className="rounded-full bg-[#40E0D0] px-4 py-2 text-sm font-bold text-[#0033A0] hover:bg-[#2fd4c4]">
                                    Apply
                                </button>
                            </div>
                            {appliedCode && (
                                <p className="mt-2 text-xs font-bold text-green-700" data-testid="discount-applied">
                                    Code {appliedCode} applied
                                </p>
                            )}
                        </div>

                        {quote ? (
                            <dl className="space-y-2 text-sm border-t border-slate-100 pt-4" data-testid="basket-totals">
                                <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-bold">{money(quote.subtotal)}</dd></div>
                                {quote.discount_amount > 0 && (
                                    <div className="flex justify-between text-green-700">
                                        <dt>Discount ({quote.discount_code})</dt><dd className="font-bold">-{money(quote.discount_amount)}</dd>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <dt className="text-slate-500">{FULFILMENT_LABELS[quote.fulfilment] || "Collection"}</dt>
                                    <dd className="font-bold">{quote.delivery_fee > 0 ? money(quote.delivery_fee) : "Free"}</dd>
                                </div>
                                <div className="flex justify-between text-lg border-t border-slate-100 pt-2">
                                    <dt className="font-heading font-extrabold">Total</dt>
                                    <dd className="font-heading font-extrabold text-[#0033A0]" data-testid="basket-total">{money(quote.total)}</dd>
                                </div>
                            </dl>
                        ) : (
                            <p className="text-sm text-slate-400 border-t border-slate-100 pt-4">Updating totals…</p>
                        )}

                        <button type="button" data-testid="checkout-button"
                            onClick={() => navigate("/checkout", { state: { fulfilment, discount_code: appliedCode } })}
                            disabled={!quote}
                            className="mt-6 w-full rounded-full bg-[#0033A0] py-3.5 font-heading font-bold text-white hover:bg-[#002277] transition-colors disabled:opacity-50">
                            Go to Checkout
                        </button>
                    </div>
                </div>
            </div>
        </main>
    );
}
